import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
    changeStatusNotification,
    getMarkAsRead,
    getNotifications,
    listSupportNotifications,
    putSupportNotificationRead,
    putSupportNotificationsReadAll,
} from './notifications.service'

export const SUPPORT_NOTIFICATIONS_PAGE_LIMIT = 15

const initialState = {
    status: 'loaded',
    notificationsData: [],
    isRead: false,
    supportListStatus: 'idle',
    supportList: [],
    supportUnreadCount: 0,
    supportHasNext: false,
    supportNextSkip: 0,
    supportListError: null,
    supportPageLimit: SUPPORT_NOTIFICATIONS_PAGE_LIMIT,
    supportUnreadFilter: false,
}

/** Normalize GET support/notifications body (result + isNext pagination). */
export const normalizeSupportNotificationsBody = (raw) => {
    const body = raw?.data ?? raw ?? {}
    const result = Array.isArray(body.result)
        ? body.result
        : Array.isArray(body.data)
          ? body.data
          : Array.isArray(body.notifications)
            ? body.notifications
            : []
    const skip = Number(body.skip) || 0
    const limit = Number(body.limit) || result.length || SUPPORT_NOTIFICATIONS_PAGE_LIMIT
    const isNext = Boolean(body.isNext)
    return {
        result,
        unreadCount: Number(body.unreadCount) || 0,
        isNext,
        skip,
        limit,
        nextSkip: isNext ? skip + result.length : skip,
    }
}

export const notificationsGet = createAsyncThunk(
    'notificationsSlice/getScreenShots',
    async (payload) => {
        const response = await getNotifications(payload)
        return response
    }
)

export const statusChangeNotification = createAsyncThunk(
    'notificationsSlice/changeStatusNotification',
    async (payload) => {
        const response = await changeStatusNotification(payload)
        return response
    }
)

export const markAsRead = createAsyncThunk(
    'notificationsSlice/getMarkAsRead',
    async (payload) => {
        const response = await getMarkAsRead(payload)
        return response
    }
)

export const fetchSupportNotifications = createAsyncThunk(
    'notificationsSlice/fetchSupportNotifications',
    async (
        { skip = 0, limit = 20, reset = true, unRead } = {},
        { rejectWithValue }
    ) => {
        try {
            const response = await listSupportNotifications({
                skip,
                limit,
                unRead,
            })
            return {
                data: response?.data,
                reset,
                requestSkip: skip,
                requestLimit: limit,
                unRead: Boolean(unRead),
            }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    error?.message ||
                    'Failed to load notifications'
            )
        }
    }
)

export const markSupportNotificationRead = createAsyncThunk(
    'notificationsSlice/markSupportNotificationRead',
    async (notificationId, { rejectWithValue }) => {
        try {
            const response = await putSupportNotificationRead(notificationId)
            return {
                notificationId,
                data: response?.data,
            }
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    error?.message ||
                    'Failed to mark notification read'
            )
        }
    }
)

export const markAllSupportNotificationsRead = createAsyncThunk(
    'notificationsSlice/markAllSupportNotificationsRead',
    async (_, { rejectWithValue }) => {
        try {
            const response = await putSupportNotificationsReadAll()
            return response?.data
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.message ||
                    error?.message ||
                    'Failed to mark all notifications read'
            )
        }
    }
)

export const notificationsSlice = createSlice({
    name: 'notificationsSlice',
    initialState,
    reducers: {
        setReadNotification(state) {
            state.isRead = state.isRead ? false : true
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(notificationsGet.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(notificationsGet.fulfilled, (state, action) => {
                state.status = 'loaded'
                state.notificationsData = action.payload.data
            })
            .addCase(fetchSupportNotifications.pending, (state, action) => {
                const reset = action.meta.arg?.reset !== false
                state.supportListStatus = reset ? 'loading' : 'loadingMore'
                if (reset) state.supportListError = null
            })
            .addCase(fetchSupportNotifications.fulfilled, (state, action) => {
                state.supportListStatus = 'loaded'
                const payload = action.payload || {}
                const {
                    result,
                    unreadCount,
                    isNext,
                    nextSkip,
                    limit,
                } = normalizeSupportNotificationsBody(payload.data)
                const reset = payload.reset !== false

                state.supportPageLimit =
                    limit || payload.requestLimit || SUPPORT_NOTIFICATIONS_PAGE_LIMIT
                if (reset) {
                    state.supportUnreadFilter = Boolean(payload.unRead)
                }
                if (reset) {
                    state.supportList = result
                } else {
                    const seen = new Set(
                        state.supportList.map((n) => String(n?._id))
                    )
                    const merged = [...state.supportList]
                    result.forEach((n) => {
                        const id = n?._id != null ? String(n._id) : null
                        if (id && !seen.has(id)) {
                            seen.add(id)
                            merged.push(n)
                        }
                    })
                    state.supportList = merged
                }
                state.supportUnreadCount = unreadCount
                state.supportHasNext = isNext
                state.supportNextSkip = isNext ? nextSkip : state.supportList.length
                state.supportListError = null
            })
            .addCase(fetchSupportNotifications.rejected, (state, action) => {
                state.supportListStatus = 'error'
                state.supportListError =
                    action.payload || 'Failed to load notifications'
            })
            .addCase(markSupportNotificationRead.fulfilled, (state, action) => {
                const id = action.payload?.notificationId
                const body = action.payload?.data || {}
                const row = state.supportList.find(
                    (n) => String(n?._id) === String(id)
                )
                if (row?.notificationData && !row.notificationData.isReaded) {
                    row.notificationData.isReaded = true
                    state.supportUnreadCount = Math.max(
                        0,
                        state.supportUnreadCount - 1
                    )
                }
                if (typeof body.unreadCount === 'number') {
                    state.supportUnreadCount = body.unreadCount
                }
            })
            .addCase(markAllSupportNotificationsRead.fulfilled, (state, action) => {
                const body = action.payload || {}
                state.supportList.forEach((n) => {
                    if (n?.notificationData) {
                        n.notificationData.isReaded = true
                    }
                })
                if (typeof body.unreadCount === 'number') {
                    state.supportUnreadCount = body.unreadCount
                } else {
                    state.supportUnreadCount = 0
                }
            })
    },
})

export default notificationsSlice.reducer

export const notificationsState = (state) => {
    const { notificationsSlice } = state
    const { status, notificationsData, isRead } = notificationsSlice
    return { status, notificationsData, isRead }
}

export const supportNotificationsState = (state) => {
    const s = state.notificationsSlice
    return {
        list: s.supportList,
        status: s.supportListStatus,
        unreadCount: s.supportUnreadCount,
        hasNext: s.supportHasNext,
        nextSkip: s.supportNextSkip,
        error: s.supportListError,
        pageLimit: s.supportPageLimit,
        unreadFilter: s.supportUnreadFilter,
    }
}

export const { setReadNotification } = notificationsSlice.actions
